/**
 * Gmail Service
 *
 * Unified microservice for Gmail integration
 * Separate OAuth flow for adding Gmail accounts
 */

import { supabase } from './supabaseClient';
import { scanGmailForSubscriptions } from './gmailScanService';
import { deleteAIInsightsForGmail } from './aiDataCleanup';
import { addAccountToRefreshService } from './gmailTokenRefreshService';

const GMAIL_SCOPES = [
  'https://www.googleapis.com/auth/gmail.readonly',
  'https://www.googleapis.com/auth/userinfo.email'
].join(' ');

/**
 * Initiate Gmail OAuth flow in popup
 * Opens OAuth popup for user to select Gmail account
 * @returns {Promise<void>}
 */
export const connectGmailAccount = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No authenticated user');

    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = `${window.location.origin}/gmail-callback`;

    if (!clientId) {
      throw new Error('REACT_APP_GOOGLE_CLIENT_ID is not set. Please restart your dev server after adding it to .env');
    }

    // Create state for CSRF protection
    const state = btoa(JSON.stringify({
      userId: user.id,
      timestamp: Date.now(),
      nonce: crypto.randomUUID()
    }));

    localStorage.setItem('gmail_oauth_state', state);

    // Build OAuth URL
    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    authUrl.searchParams.set('client_id', clientId);
    authUrl.searchParams.set('redirect_uri', redirectUri);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('scope', GMAIL_SCOPES);
    authUrl.searchParams.set('access_type', 'offline');
    authUrl.searchParams.set('prompt', 'consent select_account');
    authUrl.searchParams.set('state', state);

    // Open OAuth popup
    const width = 500;
    const height = 600;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    const popup = window.open(
      authUrl.toString(),
      'Gmail OAuth',
      `width=${width},height=${height},left=${left},top=${top}`
    );

    if (!popup) {
      throw new Error('Popup was blocked! Please allow popups for this site.');
    }

    return popup;
  } catch (error) {
    throw error;
  }
};

// Alias that makes intent clear — returns popup reference for tracking
export const connectGmailAccountWithPopupRef = connectGmailAccount;

/**
 * Handle OAuth callback and save Gmail account
 * @param {string} code - OAuth authorization code
 * @param {string} state - State parameter for validation
 * @returns {Promise<Object>} Gmail account data
 */
export const completeGmailConnection = async (code, state) => {
  try {
    // State is stored in localStorage (shared across same-origin windows)
    // COOP may block window.opener, but localStorage is always accessible
    const storedState = localStorage.getItem('gmail_oauth_state');

    if (!storedState || storedState !== state) {
      throw new Error('Invalid state parameter - security check failed');
    }

    // Decode state to check if this is a re-authentication
    let stateData = null;
    try {
      stateData = JSON.parse(atob(state));
    } catch (e) {
      // State data not decodable — proceed without it
    }

    // Clean up stored state
    localStorage.removeItem('gmail_oauth_state');

    // Ensure we have a valid user session before calling Edge Function
    // The popup window needs time to restore session from localStorage
    let popupSession = null;
    for (let attempt = 0; attempt < 10; attempt++) {
      const { data } = await supabase.auth.getSession();
      if (data?.session?.access_token) {
        popupSession = data.session;
        break;
      }
      console.log(`⏳ Waiting for session to restore... (attempt ${attempt + 1}/10)`);
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    if (!popupSession) {
      throw new Error('No active user session in popup - please try again');
    }

    // Exchange code for tokens via server-side Edge Function (client_secret stays server-side)
    const { data: tokenResponseData, error: tokenError } = await supabase.functions.invoke('gmail-oauth-exchange', {
      body: {
        grant_type: 'authorization_code',
        code,
        redirect_uri: `${window.location.origin}/gmail-callback`,
      }
    });

    if (tokenError || tokenResponseData?.error) {
      throw new Error(tokenResponseData?.error || tokenError?.message || 'Token exchange failed');
    }

    const tokens = tokenResponseData;

    // Get Gmail user info
    const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
      headers: { 'Authorization': `Bearer ${tokens.access_token}` }
    });

    if (!userInfoResponse.ok) {
      throw new Error('Failed to fetch Gmail user info');
    }

    const userInfo = await userInfoResponse.json();

    // Get current session and user
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    const { data: { user }, error: userError } = await supabase.auth.getUser();

    console.log('🔑 Session info in callback:', {
      hasSession: !!session,
      hasUser: !!user,
      userId: user?.id,
      userEmail: user?.email,
      sessionValid: session?.expires_at ? new Date(session.expires_at * 1000) > new Date() : false,
      accessToken: session?.access_token ? 'present' : 'missing'
    });

    if (sessionError || !session) {
      throw new Error('No session in callback popup. Session may not be shared across windows.');
    }

    if (userError || !user) {
      throw new Error('No authenticated user in callback. User may need to be logged in.');
    }

    // Save Gmail account (upsert will update if exists, insert if new)
    const isReauth = stateData?.isReauth || false;
    console.log('💾 Attempting to save Gmail account:', {
      user_id: user.id,
      gmail_email: userInfo.email,
      isReauth: isReauth
    });

    const { data: gmailAccount, error: dbError } = await supabase
      .from('synced_gmail_accounts')
      .upsert({
        user_id: user.id,
        gmail_email: userInfo.email,
        gmail_refresh_token: tokens.refresh_token,
        sync_status: 'pending',
        sync_error: null, // Clear any previous errors
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id,gmail_email'
      })
      .select()
      .single();

    if (dbError) {
      console.error('❌ Database INSERT failed:', {
        error: dbError,
        code: dbError.code,
        message: dbError.message,
        details: dbError.details
      });
      throw new Error(`Database error: ${dbError.message} (code: ${dbError.code})`);
    }

    if (!gmailAccount) {
      console.error('❌ No Gmail account returned from database');
      throw new Error('Failed to save Gmail account - no data returned');
    }

    if (isReauth) {
      console.log('✅ Gmail account re-authenticated successfully:', gmailAccount);
    } else {
      console.log('✅ Gmail account connected successfully:', gmailAccount);
    }

    // Add account to token refresh service to keep tokens fresh
    try {
      addAccountToRefreshService(gmailAccount);
    } catch (refreshError) {
      console.warn('⚠️ Could not add account to refresh service:', refreshError);
      // Don't throw - account is still connected successfully
    }

    return gmailAccount;
  } catch (error) {
    console.error('Error completing Gmail connection:', error);
    throw error;
  }
};

/**
 * Get all synced Gmail accounts with stats
 * @returns {Promise<Array>} Gmail accounts with subscription counts
 */
export const getGmailAccounts = async () => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { data: accounts, error } = await supabase
      .from('synced_gmail_accounts')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching synced Gmail accounts:', error);
      throw error;
    }

    // For each account, count the subscriptions imported from that Gmail
    const accountsWithCounts = await Promise.all(
      (accounts || []).map(async (account) => {
        const { count, error: countError } = await supabase
          .from('subscriptions')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('imported_from_email', account.gmail_email);

        if (countError) {
          console.error(`Error counting subscriptions for ${account.gmail_email}:`, countError);
        }

        return {
          ...account,
          subscriptions_count: count || 0
        };
      })
    );

    return accountsWithCounts;
  } catch (error) {
    console.error('getGmailAccounts error:', error);
    throw error;
  }
};

/**
 * Sync a Gmail account (scan for subscriptions)
 * @param {string} gmailAccountId - Gmail account ID
 * @returns {Promise<Object>} Sync results
 */
export const syncGmailAccount = async (gmailAccountId) => {
  try {
    console.log('🔄 Starting Gmail sync for account:', gmailAccountId);
    return await scanGmailForSubscriptions(gmailAccountId);
  } catch (error) {
    console.error('Error syncing Gmail account:', error);
    throw error;
  }
};

/**
 * Sync all Gmail accounts for the current user
 * @returns {Promise<Object>} Sync results for all accounts
 */
export const syncAllGmailAccounts = async () => {
  try {
    console.log('🔄 Starting sync for all Gmail accounts');

    // Get all Gmail accounts for current user
    const accounts = await getGmailAccounts();

    if (!accounts || accounts.length === 0) {
      console.log('ℹ️ No Gmail accounts to sync');
      return { synced: 0, total: 0 };
    }

    console.log(`📧 Found ${accounts.length} Gmail account(s) to sync`);

    // Sync each account
    const results = await Promise.allSettled(
      accounts.map(account => syncGmailAccount(account.id))
    );

    const successful = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;

    console.log(`✅ Sync complete: ${successful} successful, ${failed} failed`);

    return {
      synced: successful,
      failed: failed,
      total: accounts.length
    };
  } catch (error) {
    console.error('Error syncing all Gmail accounts:', error);
    throw error;
  }
};

/**
 * Re-authenticate a Gmail account to refresh expired tokens
 * Redirects in the same tab (no popup) for better UX
 * @param {string} gmailAccountId - Gmail account ID to re-authenticate
 * @returns {Promise<void>}
 */
export const reauthenticateGmailAccount = async (gmailAccountId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No authenticated user');

    // Get the Gmail account to re-authenticate
    const { data: gmailAccount, error: fetchError } = await supabase
      .from('synced_gmail_accounts')
      .select('*')
      .eq('id', gmailAccountId)
      .single();

    if (fetchError) throw fetchError;

    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = `${window.location.origin}/gmail-callback`;

    if (!clientId) {
      throw new Error('REACT_APP_GOOGLE_CLIENT_ID is not set');
    }

    // Create state with reauth flag
    const state = btoa(JSON.stringify({
      userId: user.id,
      gmailAccountId: gmailAccountId,
      isReauth: true,
      timestamp: Date.now(),
      nonce: crypto.randomUUID()
    }));

    localStorage.setItem('gmail_oauth_state', state);

    // Build OAuth URL with login_hint to suggest the correct email
    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    authUrl.searchParams.set('client_id', clientId);
    authUrl.searchParams.set('redirect_uri', redirectUri);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('scope', GMAIL_SCOPES);
    authUrl.searchParams.set('access_type', 'offline');
    authUrl.searchParams.set('prompt', 'consent'); // Force consent to get new refresh token
    authUrl.searchParams.set('login_hint', gmailAccount.gmail_email);
    authUrl.searchParams.set('state', state);

    console.log('🔗 Redirecting to Gmail re-authentication for:', gmailAccount.gmail_email);

    // Same-tab redirect — no popup needed
    window.location.href = authUrl.toString();
  } catch (error) {
    console.error('❌ Error initiating Gmail re-authentication:', error);
    throw error;
  }
};

/**
 * Sync Gmail for a specific subscription to check for cancellation confirmations
 * Used after user completes cancellation to detect confirmation emails
 * @param {string} subscriptionId - Subscription ID to sync
 * @returns {Promise<void>}
 */
export const syncSpecificSubscription = async (subscriptionId) => {
  try {
    console.log('🔄 Starting Gmail sync for subscription:', subscriptionId);

    // Get all Gmail accounts for the user
    const gmailAccounts = await getGmailAccounts();

    if (!gmailAccounts || gmailAccounts.length === 0) {
      console.log('No Gmail accounts connected, skipping sync');
      return;
    }

    // Sync all Gmail accounts (the scanner will look for cancellation emails)
    // This could be optimized to only scan recent emails for this specific subscription
    for (const account of gmailAccounts) {
      await scanGmailForSubscriptions(account.id);
    }

    console.log('✓ Gmail sync completed for subscription');
  } catch (error) {
    console.error('Error syncing Gmail for subscription:', error);
    // Don't throw - this is a background operation that shouldn't block the user
  }
};

/**
 * Remove Gmail account and all associated subscriptions
 * @param {string} gmailAccountId - Gmail account ID
 * @returns {Promise<void>}
 */
export const disconnectGmailAccount = async (gmailAccountId) => {
  try {
    // Get the Gmail account first to find associated subscriptions
    const { data: gmailAccount, error: fetchError } = await supabase
      .from('synced_gmail_accounts')
      .select('gmail_email')
      .eq('id', gmailAccountId)
      .single();

    if (fetchError) throw fetchError;

    const gmailEmail = gmailAccount.gmail_email;
    console.log('🗑️ Removing Gmail account and all associated data:', gmailEmail);

    // Delete AI Insights data BEFORE deleting subscriptions (needs subscription IDs)
    try {
      await deleteAIInsightsForGmail(gmailEmail);
    } catch (aiError) {
      console.error('Error deleting AI insights for Gmail:', aiError);
      // Don't throw - continue with subscription deletion
    }

    // Delete all subscriptions imported from this Gmail
    const { error: subError } = await supabase
      .from('subscriptions')
      .delete()
      .eq('imported_from_email', gmailEmail);

    if (subError) {
      console.error('Error deleting Gmail subscriptions:', subError);
      throw subError;
    }

    // Delete the Gmail account record
    const { error: deleteError } = await supabase
      .from('synced_gmail_accounts')
      .delete()
      .eq('id', gmailAccountId);

    if (deleteError) {
      console.error('Error removing Gmail account:', deleteError);
      throw deleteError;
    }

    console.log('✅ Successfully removed Gmail account and all data:', gmailEmail);
  } catch (error) {
    console.error('disconnectGmailAccount error:', error);
    throw error;
  }
};

export default {
  connectGmailAccount,
  connectGmailAccountWithPopupRef,
  getGmailAccounts,
  syncGmailAccount,
  syncAllGmailAccounts,
  disconnectGmailAccount,
  reauthenticateGmailAccount
};
